import React, { useRef } from 'react';
import { FieldArray, FieldArrayRenderProps, Formik, FormikProps } from 'formik';
import { Modal, Row, Col, Button, Divider } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { Form } from 'formik-antd';
import * as Yup from 'yup';
import cx from 'classnames';

import SubmitButton from 'components/formik/SubmitButton';
import BaseAlert from 'components/wrappers/BaseAlert';
import { AdditionalInfoSectionEnum } from 'features/account/enums';
import ContactsSection from 'components/modals/sections/ContactsSection';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { updateSupervisorInformation } from 'redux/reducers/accountSlice';
import { updateCorporateTASupervisorInformation } from 'redux/reducers/recruiterSlice';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { BaseModal, BaseModalFooter } from 'components/wrappers/BaseModal';
import { ProfileCorporateSupervisorInfoModel } from 'api/backofficeApiClient';
import useCanRemoveContact from 'features/account/AdditionalInfo/hooks/useCanRemoveContact';
import {
  isAddButtonVisible,
  isRemoveButtonVisible,
} from 'utils/fieldArrayUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';

import styles from './EditSupervisorInfoModal.module.less';

type Props = {
  onClose: () => void;
  onEdit: () => Promise<void>;
  companyId?: string;
  supervisorInfo?: ProfileCorporateSupervisorInfoModel[];
};

interface SupervisorFormValues {
  id?: string;
  firstName: string;
  lastName: string;
  title: string;
  email: string;
  phone: string;
}

interface SupervisorInfoFormValues {
  supervisors: Array<SupervisorFormValues>;
}

const supervisorValidationSchema = {
  firstName: Yup.string()
    .trim()
    .max(120, 'First name should be 120 characters maximum'),
  lastName: Yup.string()
    .trim()
    .max(120, 'Last name should be 120 characters maximum'),
  title: Yup.string().trim().max(120, 'Title should be 120 characters maximum'),
  email: Yup.string()
    .email('Email format is invalid')
    .max(120, 'Email should be 120 characters maximum'),
  phone: Yup.string()
    .trim()
    .max(120, 'Phone number should be 120 characters maximum'),
};

const validationSchema = Yup.object({
  supervisors: Yup.array().of(
    Yup.object({
      ...supervisorValidationSchema,
      firstName:
        supervisorValidationSchema.firstName.required('Field is required'),
      lastName:
        supervisorValidationSchema.lastName.required('Field is required'),
      title: supervisorValidationSchema.title.required('Field is required'),
      email: supervisorValidationSchema.email.required('Field is required'),
      phone: supervisorValidationSchema.phone.required('Field is required'),
    }),
  ),
});

const SupervisorsFieldArray = React.memo(
  (props: FieldArrayRenderProps) => {
    const { supervisors } = (
      props.form as FormikProps<SupervisorInfoFormValues>
    ).values;
    const [canRemoveOptions, checkCanRemoveContact] = useCanRemoveContact(
      AdditionalInfoSectionEnum.Supervisor,
    );

    const handleRemoveContact = async (index: number) => {
      const { id } = supervisors[index];
      const canContactBeRemoved = await checkCanRemoveContact(id);

      canContactBeRemoved && removeContactFromArray(index);
    };

    const removeContactFromArray = (index: number) => {
      props.remove(index);
    };

    const isContactChecking = (supervisorId?: string) => {
      const { isLoading, contactId } = canRemoveOptions;

      return isLoading && contactId === supervisorId;
    };

    return (
      <>
        {supervisors.length > 0 &&
          supervisors.map((supervisor: SupervisorFormValues, index: number) => (
            <div key={index} className={styles.supervisorSection}>
              <ContactsSection
                index={index}
                formNamePrefix={props.name}
                actionButtons={
                  <>
                    {isAddButtonVisible<SupervisorFormValues>(
                      supervisors,
                      index,
                    ) && (
                      <Button
                        className={cx('borderless-btn', styles.btn)}
                        onClick={() =>
                          props.push({
                            firstName: '',
                            lastName: '',
                            title: '',
                            email: '',
                            phone: '',
                          })
                        }
                        icon={<span className="icon icon-plus-filled"></span>}
                      />
                    )}

                    {isRemoveButtonVisible<SupervisorFormValues>(
                      supervisors,
                    ) && (
                      <Button
                        disabled={canRemoveOptions.isLoading}
                        className={cx('borderless-btn', styles.btn)}
                        onClick={() => handleRemoveContact(index)}
                        icon={
                          isContactChecking(supervisor.id) ? (
                            <LoadingOutlined />
                          ) : (
                            <span className="icon icon-closed-filled" />
                          )
                        }
                      />
                    )}
                  </>
                }
              />

              <Divider className="modal-divider after-form" />
            </div>
          ))}
      </>
    );
  },
  (prevProps: FieldArrayRenderProps, nextProps: FieldArrayRenderProps) => {
    return (
      (prevProps.form.values as SupervisorInfoFormValues).supervisors.length ===
      (nextProps.form.values as SupervisorInfoFormValues).supervisors.length
    );
  },
);

export default function EditSupervisorInfoModal(props: Props) {
  const { companyId, supervisorInfo, onClose, onEdit } = props;
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const formRef: any = useRef();
  const initialValues: SupervisorInfoFormValues = {
    supervisors: !!supervisorInfo?.length
      ? supervisorInfo.map(
          (supervisor): SupervisorFormValues => ({
            id: supervisor.supervisorId,
            firstName: supervisor.firstName || '',
            lastName: supervisor.lastName || '',
            email: supervisor.email || '',
            title: supervisor.title || '',
            phone: supervisor.phoneNumber || '',
          }),
        )
      : [
          {
            firstName: '',
            lastName: '',
            title: '',
            email: '',
            phone: '',
          },
        ],
  };

  const handleSubmit = async (values: SupervisorInfoFormValues) => {
    const { supervisors } = values;

    const supervisorInfoData = supervisors.map(
      (supervisor): ProfileCorporateSupervisorInfoModel => ({
        supervisorId: supervisor.id,
        firstName: supervisor.firstName || undefined,
        lastName: supervisor.lastName || undefined,
        email: supervisor.email || undefined,
        title: supervisor.title || undefined,
        phoneNumber: supervisor.phone || undefined,
      }),
    );

    const correctAction = isAdmin
      ? updateCorporateTASupervisorInformation
      : updateSupervisorInformation;
    const requestData = {
      companyId: isAdmin ? companyId : undefined,
      supervisorsData: { supervisors: supervisorInfoData },
    };
    const result = await dispatch(correctAction(requestData));

    if (correctAction.fulfilled.match(result)) {
      await onEdit();

      BaseAlert.success('Supervisor information saved.');
      onClose();
    } else {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }
  };

  const handleModalCancel = () => {
    if (formRef.current?.isSubmitting) return;

    if (formRef.current?.dirty) {
      return Modal.confirm({
        title: 'Are you sure? The data will not be saved.',
        icon: <span className="icon icon-attention-filled" />,
        onOk() {
          onClose();
        },
        onCancel() {},
      });
    }
    onClose();
  };

  return (
    <BaseModal
      title="Edit supervisor information"
      maxWidth={888}
      open={true}
      onCancel={handleModalCancel}
    >
      <Formik
        enableReinitialize
        innerRef={formRef}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        <Form layout="vertical">
          <div className="modal-scroll-wrapper">
            <Row gutter={48} align="middle">
              <Col xs={24} md={7}>
                <div className="modal-title">Supervisor</div>
              </Col>
            </Row>

            <FieldArray
              name="supervisors"
              component={SupervisorsFieldArray as any}
            />
          </div>

          <BaseModalFooter>
            <Button onClick={handleModalCancel}>Cancel</Button>

            <SubmitButton type="primary">Save</SubmitButton>
          </BaseModalFooter>
        </Form>
      </Formik>
    </BaseModal>
  );
}
